const MONTHS = [
  ["jan", "january"], ["feb", "february"], ["mar", "march"], ["apr", "april"],
  ["may"], ["jun", "june"], ["jul", "july"], ["aug", "august"],
  ["sep", "sept", "september"], ["oct", "october"], ["nov", "november"], ["dec", "december"],
];

const pad = (value: number): string => String(value).padStart(2, "0");

function calendarDate(year: number, month: number, day: number): string | null {
  if (!Number.isInteger(year) || year < 1 || year > 9999 || month < 1 || month > 12) return null;
  const leap = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const days = [31, leap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]!;
  if (day < 1 || day > days) return null;
  return `${String(year).padStart(4, "0")}-${pad(month)}-${pad(day)}`;
}

// Preserve the written calendar day of an ISO timestamp. Converting through
// Date would shift transactions near midnight when the browser's timezone differs.
// Older ledger entries omit a year, so their caller supplies the fallback year.
export function financeDateIso(date: string, fallbackYear = new Date().getFullYear()): string | null {
  const value = String(date ?? "").trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)?)?$/i.exec(value);
  if (iso) return calendarDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const display = /^([A-Za-z]+)\.?\s+(\d{1,2})(?:(?:,\s*|\s+)(\d{4}))?$/.exec(value);
  if (!display) return null;
  const month = MONTHS.findIndex((names) => names.includes(display[1]!.toLowerCase())) + 1;
  return calendarDate(display[3] ? Number(display[3]) : fallbackYear, month, Number(display[2]));
}

export function isWithinFinanceDateRange(
  date: string,
  start: string,
  end: string,
  fallbackYear = new Date().getFullYear(),
): boolean {
  const from = start.trim();
  const to = end.trim();
  // Clearing the picker restores every row, including old unparseable dates.
  if (!from && !to) return true;
  const startIso = from ? financeDateIso(from, fallbackYear) : null;
  const endIso = to ? financeDateIso(to, fallbackYear) : null;
  if ((from && !startIso) || (to && !endIso) || (startIso && endIso && startIso > endIso)) return false;
  const iso = financeDateIso(date, fallbackYear);
  return iso !== null && (!startIso || iso >= startIso) && (!endIso || iso <= endIso);
}
